import { useEffect, useState, type FormEvent } from "react";
import Modal from "@modalSrc/Modal";
import RightTimeApi from "@/API";
import { toast } from "react-toastify";
import { BsQrCode } from "react-icons/bs";
import { FaPlusSquare } from "react-icons/fa";
import { IoTrashOutline } from "react-icons/io5";
import { MdCheckBox, MdCheckBoxOutlineBlank } from "react-icons/md";
import { copyUrlAsLink } from "@/utils/copyUrlAsLink";
import DateTimePicker from "@elements/DateTimePicker";
import ConfirmModal from "./ConfirmModal";
import {
  applyCttCodeActiveToggle,
  classifyCttCodeStatus,
  cttCodeDatesForApi,
} from "@/utils/cttCodeWindow";

export type CttCodeFieldValue = {
  companyTestTypeFieldId?: number;
  label?: string;
  display?: string;
};

export type CttCodeRow = {
  id?: number;
  code?: string;
  startDateTime?: string | null;
  endDateTime?: string | null;
  status?: string;
  canShareUrl?: boolean;
  fieldValues?: CttCodeFieldValue[];
};

type CttCodeEditModalProps = {
  isOpen: boolean;
  cttId: number | string;
  codeRow: CttCodeRow | null;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
  onShowQr?: (code: string) => void;
};

const CODE_MAX_LENGTH = 10;
const CODE_GENERATE_CHARS = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";

function normalizeUtcIso(value: string | null | undefined): string {
  if (!value) return "";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "" : d.toISOString();
}

function isStartAfterEnd(startUtc: string, endUtc: string): boolean {
  if (!startUtc || !endUtc) return false;
  return new Date(startUtc) > new Date(endUtc);
}

function generateCttCode(): string {
  let suffix = "";
  for (let i = 0; i < 5; i += 1) {
    const idx = Math.floor(Math.random() * CODE_GENERATE_CHARS.length);
    suffix += CODE_GENERATE_CHARS[idx];
  }
  return `CTT${suffix}`;
}

function readPickerValue(e: { target?: { value?: string | null } }): string {
  const raw = e?.target?.value;
  if (raw == null || raw === "") return "";
  return normalizeUtcIso(String(raw));
}

export default function CttCodeEditModal({
  isOpen,
  cttId,
  codeRow,
  onClose,
  onSaved,
  onShowQr,
}: CttCodeEditModalProps) {
  const isEdit = !!(codeRow?.id && codeRow.id > 0);
  const [codeValue, setCodeValue] = useState("");
  const [startUtc, setStartUtc] = useState("");
  const [endUtc, setEndUtc] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setCodeValue(codeRow?.code || "");
    setStartUtc(normalizeUtcIso(codeRow?.startDateTime));
    setEndUtc(normalizeUtcIso(codeRow?.endDateTime));
    setShowDeleteConfirm(false);
  }, [isOpen, codeRow]);

  if (!isOpen) return null;

  const liveStatus = classifyCttCodeStatus(startUtc, endUtc);

  async function onToggleActive() {
    if (isSaving) return;

    const currentlyActive = liveStatus === "Active";
    const next = applyCttCodeActiveToggle(startUtc, endUtc, currentlyActive);
    const previousStart = startUtc;
    const previousEnd = endUtc;

    setStartUtc(next.startUtc);
    setEndUtc(next.endUtc);

    if (!isEdit || !codeRow?.id) {
      return;
    }

    setIsSaving(true);
    try {
      const resp = await RightTimeApi.putAsync(
        `/CompanyTestType/${cttId}/Codes/${codeRow.id}`,
        cttCodeDatesForApi(next.startUtc, next.endUtc)
      );
      if (!resp?.ok) {
        setStartUtc(previousStart);
        setEndUtc(previousEnd);
        toast.error(resp?.jsonData?.message || "Failed to update code status");
        return;
      }
      toast.success(currentlyActive ? "Code deactivated" : "Code activated");
      await onSaved();
    } catch (error) {
      console.error("Error toggling CTT code status:", error);
      setStartUtc(previousStart);
      setEndUtc(previousEnd);
      toast.error("Failed to update code status");
    } finally {
      setIsSaving(false);
    }
  }

  async function pasteCodeLink() {
    const code = isEdit ? codeRow?.code : codeValue;
    if (!code) return;
    const url = import.meta.env.VITE_TEST_RUNNER_URL + code;
    const copied = await copyUrlAsLink(url);
    if (copied) toast.success("Company Test Type URL copied to clipboard");
    else toast.error("Could not copy URL to clipboard");
  }

  function onGenerateCode() {
    setCodeValue(generateCttCode());
  }

  function onStartChange(e: { target?: { value?: string | null } }) {
    const value = readPickerValue(e);
    if (isStartAfterEnd(value, endUtc)) {
      toast.error("Start must be before End");
      setStartUtc("");
      return;
    }
    setStartUtc(value);
  }

  function onEndChange(e: { target?: { value?: string | null } }) {
    const value = readPickerValue(e);
    if (isStartAfterEnd(startUtc, value)) {
      toast.error("End must be after Start");
      setEndUtc("");
      return;
    }
    setEndUtc(value);
  }

  async function handleSave(e?: FormEvent) {
    e?.preventDefault();
    if (!cttId || isSaving) return;

    if (isStartAfterEnd(startUtc, endUtc)) {
      toast.error("Start must be before End");
      return;
    }

    const trimmedCode = codeValue.trim();
    if (!isEdit) {
      if (!trimmedCode) {
        toast.error("Code is required");
        return;
      }
      if (trimmedCode.length > CODE_MAX_LENGTH) {
        toast.error(`Code must be at most ${CODE_MAX_LENGTH} characters`);
        return;
      }
    }

    const startDateTime = startUtc || null;
    const endDateTime = endUtc || null;

    setIsSaving(true);
    try {
      if (isEdit && codeRow?.id) {
        const resp = await RightTimeApi.putAsync(`/CompanyTestType/${cttId}/Codes/${codeRow.id}`, {
          startDateTime,
          endDateTime,
        });
        if (!resp?.ok) {
          toast.error(resp?.jsonData?.message || "Failed to update code");
          return;
        }
        toast.success("Code updated");
      } else {
        const resp = await RightTimeApi.postAsync(`/CompanyTestType/${cttId}/Codes`, {
          code: trimmedCode,
          startDateTime,
          endDateTime,
        });
        if (!resp?.ok) {
          toast.error(resp?.jsonData?.message || "Failed to create code");
          return;
        }
        toast.success("Code created");
      }
      await onSaved();
      onClose();
    } catch (error) {
      console.error("Error saving CTT code:", error);
      toast.error("Error saving code");
    } finally {
      setIsSaving(false);
    }
  }

  async function handleDelete() {
    if (!cttId || !codeRow?.id || isSaving) return;
    setIsSaving(true);
    try {
      const resp = await RightTimeApi.deleteAsync(`/CompanyTestType/${cttId}/Codes/${codeRow.id}`);
      if (!resp?.ok) {
        toast.error("Failed to remove code");
        return;
      }
      toast.success("Code removed");
      setShowDeleteConfirm(false);
      await onSaved();
      onClose();
    } catch (error) {
      console.error("Error removing CTT code:", error);
      toast.error("Error removing code");
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={isEdit ? "Edit Code" : "Add Code"}
        className="cttCodeEditModal"
        zIndex={2000}
        footer={
          <>
            <div className="footerLeft">
              {isEdit && (
                <IoTrashOutline
                  className="actionIcon deleteIcon"
                  title="Remove Code"
                  onClick={() => setShowDeleteConfirm(true)}
                />
              )}
            </div>
            <div className="footerRight">
              <button type="button" className="btn" onClick={onClose} disabled={isSaving}>
                Cancel
              </button>
              <button type="button" className="btn primaryButton" onClick={handleSave} disabled={isSaving}>
                {isSaving ? "Saving..." : "Save"}
              </button>
            </div>
          </>
        }
      >
        <div className="tableWrapper factTable">
          <table>
            <caption>
              <span className="title">Code</span>
            </caption>
            <tbody>
              <tr>
                <th className="label right">Code</th>
                <td className="cttCodeEditCodeCell">
                  {isEdit ? (
                    <>
                      {codeRow?.canShareUrl === false ? (
                        <span>{codeRow?.code || ""}</span>
                      ) : (
                        <a onClick={pasteCodeLink} title="Copy runner URL">
                          {codeRow?.code || ""}
                        </a>
                      )}
                      {codeRow?.code && codeRow.canShareUrl !== false && onShowQr && (
                        <BsQrCode
                          className="actionIcon qrCode"
                          title="View QR code"
                          onClick={() => onShowQr(codeRow.code!)}
                        />
                      )}
                    </>
                  ) : (
                    <>
                      <input
                        type="text"
                        className="cttCodeValueInput"
                        value={codeValue}
                        maxLength={CODE_MAX_LENGTH}
                        onChange={(e) => setCodeValue(e.target.value.slice(0, CODE_MAX_LENGTH))}
                        placeholder="Enter code"
                      />
                      <FaPlusSquare
                        className="actionIcon plusIcon"
                        title="Generate"
                        onClick={onGenerateCode}
                      />
                    </>
                  )}
                </td>
              </tr>
              <tr>
                <th className="label right">Start</th>
                <td className="cttCodeDateCell">
                  <DateTimePicker
                    field="StartDateTime"
                    pickerTitle="Start"
                    value={startUtc}
                    onChange={onStartChange}
                    displayAsText
                    className="editBoxes"
                  />
                  <IoTrashOutline
                    className={`actionIcon deleteIcon${!startUtc ? " disabled" : ""}`}
                    title="Clear start"
                    onClick={() => startUtc && setStartUtc("")}
                  />
                </td>
              </tr>
              <tr>
                <th className="label right">End</th>
                <td className="cttCodeDateCell">
                  <DateTimePicker
                    field="EndDateTime"
                    pickerTitle="End"
                    value={endUtc}
                    onChange={onEndChange}
                    displayAsText
                    className="editBoxes"
                  />
                  <IoTrashOutline
                    className={`actionIcon deleteIcon${!endUtc ? " disabled" : ""}`}
                    title="Clear end"
                    onClick={() => endUtc && setEndUtc("")}
                  />
                </td>
              </tr>
              <tr>
                <th className="label right">Status</th>
                <td
                  className={`cttCodeStatus status${liveStatus} actions`}
                  onClick={onToggleActive}
                  title={liveStatus === "Active" ? "Deactivate code" : "Activate code"}
                >
                  {liveStatus === "Active" ? (
                    <MdCheckBox className="checkbox checked actionIcon" />
                  ) : (
                    <MdCheckBoxOutlineBlank className="checkbox unchecked actionIcon" />
                  )}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </Modal>
      <ConfirmModal
        isOpen={showDeleteConfirm}
        onConfirm={handleDelete}
        onCancel={() => setShowDeleteConfirm(false)}
        title="Remove Code"
        message={`Remove code "${codeRow?.code || ""}"?`}
        confirmText="Yes, Remove Code"
        dangerText="Applicants will no longer be able to start tests with this code."
        zIndex={2100}
      />
    </>
  );
}
